import React, { useState, useEffect } from 'react';
import {
  Webhook,
  Plus,
  RefreshCw,
  Trash2,
  Edit,
  Play,
  CheckCircle,
  XCircle,
  ExternalLink,
  Eye,
  EyeOff,
  Copy,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  getWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  testWebhook,
  getWebhookLogs,
} from '../services/api';
import { toast } from 'sonner';

const eventTypes = [
  { id: 'all', label: 'All Events', description: 'Receive all threat notifications' },
  { id: 'dns_block', label: 'DNS Blocks', description: 'Malicious domain access blocked' },
  { id: 'file_quarantine', label: 'File Quarantine', description: 'Malicious files quarantined' },
  { id: 'email_threat', label: 'Email Threats', description: 'Phishing emails detected' },
  { id: 'app_anomaly', label: 'App Anomalies', description: 'Suspicious application behavior' },
  { id: 'critical_threat', label: 'Critical Threats', description: 'High-severity security events' },
];

export default function WebhooksPage() {
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [logs, setLogs] = useState([]);
  const [testing, setTesting] = useState(null);
  const [showSecret, setShowSecret] = useState({});
  
  const [form, setForm] = useState({
    name: '',
    url: '',
    events: ['all'],
    enabled: true,
  });

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    setLoading(true);
    try {
      const response = await getWebhooks();
      setWebhooks(response.data.webhooks || []);
    } catch (error) {
      toast.error('Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.name || !form.url) {
      toast.error('Name and URL are required');
      return;
    }
    
    try {
      await createWebhook(form);
      toast.success('Webhook created');
      setShowAddDialog(false);
      setForm({ name: '', url: '', events: ['all'], enabled: true });
      fetchWebhooks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create webhook');
    }
  };

  const handleUpdate = async () => {
    try {
      await updateWebhook(selectedWebhook.id, form);
      toast.success('Webhook updated');
      setShowEditDialog(false);
      setSelectedWebhook(null);
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to update webhook');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteWebhook(selectedWebhook.id);
      toast.success('Webhook deleted');
      setShowDeleteDialog(false);
      setSelectedWebhook(null);
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to delete webhook');
    }
  };

  const handleTest = async (webhookId) => {
    setTesting(webhookId);
    try {
      const response = await testWebhook(webhookId);
      if (response.data.success) {
        toast.success(`Test successful (Status: ${response.data.status_code})`);
      } else {
        toast.error(`Test failed: ${response.data.error || 'Unknown error'}`);
      }
    } catch (error) {
      toast.error('Failed to test webhook');
    } finally {
      setTesting(null);
    }
  };

  const handleViewLogs = async (webhook) => {
    setSelectedWebhook(webhook);
    try {
      const response = await getWebhookLogs(webhook.id);
      setLogs(response.data.logs || []);
      setShowLogsDialog(true);
    } catch (error) {
      toast.error('Failed to load logs');
    }
  };

  const handleToggleEnabled = async (webhook) => {
    try {
      await updateWebhook(webhook.id, { enabled: !webhook.enabled });
      fetchWebhooks();
      toast.success(`Webhook ${webhook.enabled ? 'disabled' : 'enabled'}`);
    } catch (error) {
      toast.error('Failed to update webhook');
    }
  };

  const handleEventChange = (eventId, checked) => {
    if (eventId === 'all') {
      setForm({ ...form, events: checked ? ['all'] : [] });
    } else {
      let newEvents = form.events.filter(e => e !== 'all');
      if (checked) {
        newEvents.push(eventId);
      } else {
        newEvents = newEvents.filter(e => e !== eventId);
      }
      setForm({ ...form, events: newEvents });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const openEditDialog = (webhook) => {
    setSelectedWebhook(webhook);
    setForm({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      enabled: webhook.enabled,
    });
    setShowEditDialog(true);
  };

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-bold text-2xl text-white">Webhooks</h1>
            <p className="text-sm text-slate-400">
              Configure webhooks to receive real-time threat notifications
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={fetchWebhooks} variant="outline" className="border-slate-700">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Webhook
            </Button>
          </div>
        </div>

        {/* Webhooks Table */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Configured Webhooks</CardTitle>
            <CardDescription>
              Webhooks receive HTTP POST requests when threat events occur
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
              </div>
            ) : webhooks.length === 0 ? (
              <div className="text-center py-12">
                <Webhook className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">No webhooks configured</h3>
                <p className="text-slate-400 mb-4">
                  Add a webhook to receive real-time notifications for security threats
                </p>
                <Button onClick={() => setShowAddDialog(true)} className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Webhook
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">URL</TableHead>
                    <TableHead className="text-slate-400">Events</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead className="text-slate-400">Secret</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map((webhook) => (
                    <TableRow key={webhook.id} className="border-slate-800">
                      <TableCell className="text-white font-medium">{webhook.name}</TableCell>
                      <TableCell>
                        <code className="text-xs text-slate-400 bg-slate-800 px-2 py-1 rounded">
                          {webhook.url.length > 40 ? webhook.url.slice(0, 40) + '...' : webhook.url}
                        </code>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {webhook.events.includes('all') ? (
                            <Badge variant="outline" className="border-slate-700 text-xs">All</Badge>
                          ) : (
                            webhook.events.slice(0, 2).map(e => (
                              <Badge key={e} variant="outline" className="border-slate-700 text-xs">
                                {e.replace('_', ' ')}
                              </Badge>
                            ))
                          )}
                          {webhook.events.length > 2 && !webhook.events.includes('all') && (
                            <Badge variant="outline" className="border-slate-700 text-xs">
                              +{webhook.events.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={webhook.enabled}
                          onCheckedChange={() => handleToggleEnabled(webhook)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <code className="text-xs text-slate-500">
                            {showSecret[webhook.id] ? webhook.secret : '••••••••'}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => setShowSecret({ ...showSecret, [webhook.id]: !showSecret[webhook.id] })}
                          >
                            {showSecret[webhook.id] ? (
                              <EyeOff className="w-3 h-3 text-slate-500" />
                            ) : (
                              <Eye className="w-3 h-3 text-slate-500" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(webhook.secret)}
                          >
                            <Copy className="w-3 h-3 text-slate-500" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleTest(webhook.id)}
                            disabled={testing === webhook.id}
                            className="h-8 px-2"
                          >
                            {testing === webhook.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Play className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewLogs(webhook)}
                            className="h-8 px-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(webhook)}
                            className="h-8 px-2"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setSelectedWebhook(webhook); setShowDeleteDialog(true); }}
                            className="h-8 px-2 text-red-500 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Event Types Info */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Available Event Types</CardTitle>
            <CardDescription>
              Choose which events trigger your webhooks
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {eventTypes.map((event) => (
                <div key={event.id} className="p-4 rounded-lg bg-slate-800/50 border border-slate-700">
                  <h4 className="text-white font-medium">{event.label}</h4>
                  <p className="text-sm text-slate-400 mt-1">{event.description}</p>
                  <code className="text-xs text-blue-400 mt-2 block">{event.id}</code>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add/Edit Webhook Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setShowEditDialog(false);
          setForm({ name: '', url: '', events: ['all'], enabled: true });
        }
      }}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">
              {showEditDialog ? 'Edit Webhook' : 'Add Webhook'}
            </DialogTitle>
            <DialogDescription>
              Configure webhook settings to receive threat notifications
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="My Webhook"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Endpoint URL</Label>
              <Input
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                placeholder="https://example.com/webhook"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Events</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {eventTypes.map((event) => (
                  <div key={event.id} className="flex items-center gap-2 p-2 rounded hover:bg-slate-800">
                    <Checkbox
                      id={event.id}
                      checked={form.events.includes(event.id) || (event.id !== 'all' && form.events.includes('all'))}
                      onCheckedChange={(checked) => handleEventChange(event.id, checked)}
                      disabled={event.id !== 'all' && form.events.includes('all')}
                    />
                    <label htmlFor={event.id} className="text-sm text-slate-300 cursor-pointer">
                      {event.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-300">Enabled</Label>
              <Switch
                checked={form.enabled}
                onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowAddDialog(false); setShowEditDialog(false); }}
              className="border-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={showEditDialog ? handleUpdate : handleCreate}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {showEditDialog ? 'Save Changes' : 'Create Webhook'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-slate-900 border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Webhook</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{selectedWebhook?.name}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Logs Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white">
              Delivery Logs - {selectedWebhook?.name}
            </DialogTitle>
            <DialogDescription>
              Recent webhook delivery attempts
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {logs.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No delivery logs yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Time</TableHead>
                    <TableHead className="text-slate-400">Event</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="border-slate-800">
                      <TableCell className="text-slate-400 text-xs">
                        {new Date(log.timestamp).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-slate-700 text-xs">
                          {log.event_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {log.success ? (
                          <div className="flex items-center gap-1 text-emerald-500">
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs">{log.status_code}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-red-500">
                            <XCircle className="w-4 h-4" />
                            <span className="text-xs">{log.error || log.status_code}</span>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
